import React, { useState, useEffect, useMemo } from 'react';
import { devError } from '../../lib/devLog';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Newspaper, Edit, Trash2, Plus, Search, X, Save, Calendar, ExternalLink, Loader2, Eye,
} from 'lucide-react';
import MarkdownContent from '../MarkdownContent';
import { withAdminAuth } from '../../lib/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  title: '',
  content: '',
  publication: '',
  category: 'Press',
  url: '',
  image_url: '',
  date: '',
  year: new Date().getFullYear(),
};

const NewsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [editing, setEditing] = useState(null);   // post being edited (or {} for new)
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const location = useLocation();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/news?limit=1000`, withAdminAuth());
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      devError('fetch news failed', err);
      toast.error('Could not load news: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  // Auto-open create form when arriving with ?new=1 (Dashboard quick action)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setEditing({ ...EMPTY });
      setShowForm(true);
    }
  }, [location.search]);

  const years = useMemo(() => {
    const set = new Set(items.map((n) => n.year).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [items]);

  const filtered = useMemo(() => {
    let out = items;
    if (yearFilter !== 'all') out = out.filter((n) => String(n.year) === String(yearFilter));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      out = out.filter((n) =>
        (n.title || '').toLowerCase().includes(q)
        || (n.publication || '').toLowerCase().includes(q)
        || (n.content || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [items, yearFilter, searchQuery]);

  const handleOpenNew = () => {
    setEditing({ ...EMPTY });
    setShowForm(true);
  };

  const handleOpenEdit = (post) => {
    setEditing({ ...post });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!editing?.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(
          `${API_URL}/api/admin/news/${editing.id}`,
          editing,
          withAdminAuth()
        );
        toast.success('News post updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/news`,
          editing,
          withAdminAuth()
        );
        toast.success('News post created');
      }
      setShowForm(false);
      setEditing(null);
      await fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Save failed: ' + (typeof detail === 'string' ? detail : err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/news/${post.id}`,
        withAdminAuth()
      );
      setItems((prev) => prev.filter((n) => n.id !== post.id));
      toast.success(`Deleted "${post.title}"`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status === 401) msg = 'Your admin session has expired. Please log in again.';
      else if (status === 403) msg = 'Security check failed (CSRF). Refresh the page and try again.';
      else if (status === 404) msg = 'Already deleted. Refreshing list.';
      else msg = typeof detail === 'string' ? detail : err.message;
      toast.error(`Could not delete: ${msg}`);
      if (status === 404) fetchItems();
    }
  };

  return (
    <div className="p-8" data-testid="news-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light mb-2 flex items-center gap-3">
            <Newspaper className="w-7 h-7" />
            News &amp; Press
          </h1>
          <p className="text-gray-600">Manage editorial posts and press coverage</p>
        </div>
        <button
          type="button"
          onClick={handleOpenNew}
          className="flex items-center gap-2 px-5 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
          data-testid="news-add-btn"
        >
          <Plus className="w-4 h-4" />
          Add Post
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title, publication, content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            data-testid="news-search"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          data-testid="news-year-filter"
        >
          <option value="all">All years ({items.length})</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500" data-testid="news-empty">
          No news posts match the current filter.
        </div>
      ) : (
        <div className="space-y-3" data-testid="news-list">
          {filtered.map((post) => (
            <div
              key={post.id}
              className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col sm:flex-row gap-5 hover:shadow-sm transition-shadow"
              data-testid={`news-row-${post.id}`}
            >
              {post.image_url ? (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full sm:w-32 sm:h-24 object-cover rounded"
                />
              ) : (
                <div className="w-full sm:w-32 sm:h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                  <Newspaper className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-lg leading-snug">{post.title}</h3>
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2 mb-2">
                  {post.date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                  )}
                  {post.publication && <span>· {post.publication}</span>}
                  {post.category && (
                    <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                      {post.category}
                    </span>
                  )}
                  {post.url && (
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      source
                    </a>
                  )}
                </div>
                {post.content && (
                  <p className="text-sm text-gray-700 line-clamp-2">{post.content}</p>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleOpenEdit(post)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                  data-testid={`news-edit-${post.id}`}
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(post)}
                  className="flex items-center gap-2 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                  data-testid={`news-delete-${post.id}`}
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      <AnimatePresence>
        {showForm && editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4"
            onClick={() => { setShowForm(false); setEditing(null); }}
            data-testid="news-form-overlay"
          >
            <motion.form
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleSave}
              className="bg-white rounded-lg shadow-xl w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto"
              data-testid="news-form"
            >
              <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-xl font-medium">
                  {editing.id ? 'Edit news post' : 'Add news post'}
                </h2>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="p-1 hover:bg-gray-100 rounded"
                  data-testid="news-form-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={editing.title || ''}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="news-field-title"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">
                      Content <span className="text-gray-400">(Markdown supported)</span>
                    </label>
                    <a
                      href="https://www.markdownguide.org/cheat-sheet/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-gray-500 underline hover:text-gray-700 inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Markdown cheatsheet
                    </a>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <textarea
                      rows={14}
                      value={editing.content || ''}
                      onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
                      placeholder={'**Bold**, *italic*, [links](https://...), bullet lists:\n\n- First\n- Second\n\n## Subheading'}
                      data-testid="news-field-content"
                    />
                    <div
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50 overflow-y-auto"
                      style={{ maxHeight: '340px' }}
                      data-testid="news-content-preview"
                    >
                      <div className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2 flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Live preview
                      </div>
                      {editing.content ? (
                        <MarkdownContent className="text-sm">
                          {editing.content}
                        </MarkdownContent>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          Preview will appear as you type…
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Publication</label>
                    <input
                      type="text"
                      value={editing.publication || ''}
                      onChange={(e) => setEditing({ ...editing, publication: e.target.value })}
                      placeholder="e.g. Arts and Collections"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      value={editing.category || 'Press'}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option>Press</option>
                      <option>Editorial</option>
                      <option>Announcement</option>
                      <option>Interview</option>
                      <option>Review</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="text"
                      value={editing.date || ''}
                      onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                      placeholder="15 July 2020"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Year</label>
                    <input
                      type="number"
                      value={editing.year || ''}
                      onChange={(e) => setEditing({ ...editing, year: parseInt(e.target.value, 10) || null })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Source URL</label>
                    <input
                      type="url"
                      value={editing.url || ''}
                      onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Image URL</label>
                    <input
                      type="url"
                      value={editing.image_url || ''}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t flex items-center justify-end gap-3 bg-gray-50 sticky bottom-0">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
                  data-testid="news-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editing.id ? 'Save changes' : 'Create post'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsManager;
